import { CHART_DATA_VIEW_KEYS } from 'cadenza/workbook/workbook-panel/settings/chart/chart-data-view-settings';
import { DATA_VIEW_KEYS } from 'cadenza/workbook/workbook-panel/settings/common-data-view-settings';
import { MAP_SETTINGS_KEYS } from 'cadenza/workbook/workbook-panel/settings/map/layer-settings/map-data-view-settings';
import { INDICATOR_SETTINGS_KEYS } from 'cadenza/workbook/workbook-panel/settings/other-views/indicator-data-view-settings';
import { TABLE_DATA_SETTINGS_KEYS } from 'cadenza/workbook/workbook-panel/settings/table/table-data-view-settings';
import type {
  AddAcquisitionLayerCommandPayload,
  AddAttributeCommandPayload,
  AddConditionCommandPayload,
  AddConditionGroupCommandPayload,
  AddDataViewCommandPayload,
  AddDataViewConditionCommandPayload,
  AddDrillThroughCommandPayload,
  AddNewViewWithDataViewCommandPayload,
  AddObjecttypesCommandPayload,
  AddQlConditionCommandPayload,
  AddSketchLayerCommandPayload,
  AddSpatialConditionCommandPayload,
  AddViewCommandPayload,
  AddWmsLayerCommandPayload,
  AddWorksheetCommandPayload,
  ChangeAttributeBundleSettingsPayload,
  ChangeConditionGroupSettingsCommandPayload,
  ChangeConditionSettingsCommandPayload,
  ChangeDataViewConditionCommandPayload,
  ChangeDataViewConditionSettingCommandPayload,
  ChangeDataViewSettingsCommandPayload,
  ChangeHierarchyLevelCommandPayload,
  ChangeLayerSettingsCommandPayload,
  ChangeMainObjecttypeCommandPayload,
  ChangeViewSettingsCommandPayload,
  ChangeVisualizationCommandPayload,
  ChangeWorkbookSettingsPayload,
  ChangeWorksheetSettingsCommandPayload,
  CompositeCommandPayload,
  ConvertConditionToQlCommandPayload,
  CopyViewCommandPayload,
  CopyWorksheetCommandPayload,
  CreateFilterFromSelectionCommandPayload,
  CreateGeocodingReportCommandPayload,
  CreateLocalAttributeWithModifiedOlapAttributeDefinitionCommandPayload,
  CreateLocalDateHierarchyCommandPayload,
  DrillDownFromSelectionCommandPayload,
  EditDrillThroughCommandPayload,
  ExtractDataViewToNewViewCommandPayload,
  ImportLayerFromRepositoryCommandPayload,
  InvertSelectionCommandPayload,
  RemoveAttributeCommandPayload,
  RemoveConditionCommandPayload,
  RemoveDataViewAttributeModificationCommandPayload,
  RemoveDataViewCommandPayload,
  RemoveDataViewConditionCommandPayload,
  RemoveDrillThroughCommandPayload,
  RemoveGeometryFromSpatialConditionCommandPayload,
  RemoveLayerCommandPayload,
  RemoveLocalAttributeCommandPayload,
  RemoveObjecttypesCommandPayload,
  RemoveViewCommandPayload,
  RemoveWorkbookAttributeModificationCommandPayload,
  RemoveWorksheetCommandPayload,
  RenameWorksheetCommandPayload,
  ReorderAttributesCommandPayload,
  ReorderConditionsCommandPayload,
  ReorderDataViewConditionSCommandPayload,
  ReorderLayersCommandPayload,
  ReorderWorksheetsCommandPayload,
  ResetDataViewAttributeModificationCommandPayload,
  ResetWorkbookAttributeModificationCommandPayload,
  SetConditionCommandPayload,
  SetConditionSettingCommandPayload,
  SetDataViewAttributeHighlightingRuleCommandPayload,
  SetDataViewAttributeModificationCommandPayload,
  SetQlConditionCommandPayload,
  SetSelectionCommandPayload,
  SetSpatialConditionCommandPayload,
  SetViewLayoutCommandPayload,
  SetWorkbookAttributeModificationCommandPayload,
  SortDataViewByAttributeCommandPayload,
  TransferConditionsCommandPayload,
  TransferSelectionFromDrillThroughCommandPayload,
  UngroupConditionGroupCommandPayload
} from 'cadenza/api-client/workbook-command-payloads';
import { assert } from 'cadenza/utils/custom-error';

/**
 * A list of command type strings used to decide if a change that a command causes is considered
 * minor. All other commands that are not in this list are considered major changes. As a result,
 * when navigating away from the workbook - for instance by closing the workbook tab - there is
 * no warning alert when the workbook has only minor changes.
 * That way newly added commands do cause major changes by default. If that's not desired, it's
 * type must be added to this list.
 *
 * Please consider to add new entries in alphabetical order to make it easy to check whether a
 * command has already been added.
 *
 * @type {string[]}
 */
const MINOR_COMMANDS = [
  'ADD_ATTRIBUTE',
  'ADD_CONDITION',
  'ADD_DATA_VIEW_CONDITION',
  'ADD_QL_CONDITION',
  'ADD_SPATIAL_CONDITION',
  'CHANGE_CONDITION_SETTINGS',
  'CHANGE_HIERARCHY_LEVEL',
  'CHANGE_LAYER_SETTINGS',
  'CONVERT_CONDITION_TO_QL',
  'CREATE_FILTER_FROM_SELECTION',
  'CHANGE_DATA_VIEW_CONDITION',
  'CHANGE_DATA_VIEW_CONDITION_SETTING',
  'DRILL_DOWN_FROM_SELECTION',
  'INVERT_SELECTION',
  'REMOVE_ATTRIBUTE',
  'REMOVE_CONDITION',
  'REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION',
  'REMOVE_DATA_VIEW_CONDITION',
  'REMOVE_GEOMETRY_FROM_SPATIAL_CONDITION',
  'REORDER_ATTRIBUTES',
  'REORDER_CONDITIONS',
  'REORDER_DATA_VIEW_CONDITIONS',
  'REORDER_LAYERS',
  'RESET_DATA_VIEW_ATTRIBUTE_MODIFICATION',
  'SET_CONDITION',
  'SET_CONDITION_SETTING',
  'SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES',
  'SET_DATA_VIEW_ATTRIBUTE_MODIFICATION',
  'SET_QL_CONDITION',
  'SET_SELECTION',
  'SET_SPATIAL_CONDITION',
  'SORT_DATA_VIEW_BY_ATTRIBUTE',
  'TRANSFER_CONDITIONS',
  'TRANSFER_SELECTION_FROM_DRILL_THROUGH'
];
/**
 * A list of data view settings keys used to decide if a change that a command causes is considered
 * minor. All other data view settings keys that are not in this list are considered major changes.
 * For more information see MINOR_COMMANDS.
 *
 * Please consider to add new entries in alphabetical order to make it easy to check whether a
 * key has already been added.
 *
 * @type {string[]}
 */
const MINOR_DATA_VIEW_SETTINGS_KEYS = [
  CHART_DATA_VIEW_KEYS.CHART_BREAK_AXIS_ON,
  CHART_DATA_VIEW_KEYS.CHART_MAX_DATA_RECORDS_STRATEGY,
  CHART_DATA_VIEW_KEYS.CHART_VALUE_LABELS_ON,
  DATA_VIEW_KEYS.COMMON_ATTRIBUTE_CLIPBOARD_EXPANDED,
  DATA_VIEW_KEYS.COMMON_COLOR,
  DATA_VIEW_KEYS.COMMON_GRADIENT,
  DATA_VIEW_KEYS.COMMON_GRADIENT_REVERSE,
  DATA_VIEW_KEYS.COMMON_PALETTE,
  DATA_VIEW_KEYS.COMMON_QUERY_EXECUTION_STRATEGY,
  DATA_VIEW_KEYS.COMMON_SELECTION_CONTEXT_MENU_ENABLED,
  DATA_VIEW_KEYS.COMMON_SELECTION_ENABLED,
  DATA_VIEW_KEYS.COMMON_SELECTION_ONE_CLICK_FILTER_ENABLED,
  DATA_VIEW_KEYS.COMMON_SPATIAL_FILTER_MODE,
  DATA_VIEW_KEYS.DATA_VIEW_NAME,
  MAP_SETTINGS_KEYS.DATA_VIEW_POLYGON_OPACITY,
  MAP_SETTINGS_KEYS.HEATMAP_GRADIENT,
  MAP_SETTINGS_KEYS.HEATMAP_GRADIENT_REVERSE,
  MAP_SETTINGS_KEYS.HEATMAP_OPACITY,
  MAP_SETTINGS_KEYS.HEATMAP_RADIUS_UNIT,
  MAP_SETTINGS_KEYS.HEATMAP_RADIUS_VALUE,
  MAP_SETTINGS_KEYS.NUMERIC_CLASSIFICATION_LABEL_MODE,
  MAP_SETTINGS_KEYS.PIE_CHART_SHOW_TOTALS,
  MAP_SETTINGS_KEYS.PIE_CHART_SIZE,
  MAP_SETTINGS_KEYS.PIE_CHART_VALUE_LABELS,
  MAP_SETTINGS_KEYS.SCALE_RANGE_MAX,
  MAP_SETTINGS_KEYS.SCALE_RANGE_MIN,
  INDICATOR_SETTINGS_KEYS.BOTTOM_LINK,
  INDICATOR_SETTINGS_KEYS.BOTTOM_TEXT,
  INDICATOR_SETTINGS_KEYS.TOP_LINK,
  INDICATOR_SETTINGS_KEYS.TOP_TEXT,
  INDICATOR_SETTINGS_KEYS.SHOW_BOTTOM_TEXT,
  INDICATOR_SETTINGS_KEYS.SHOW_TOP_TEXT,
  TABLE_DATA_SETTINGS_KEYS.TABLE_DATA_VIEW_SHOW_GRAND_TOTAL_FOR_COLUMNS,
  TABLE_DATA_SETTINGS_KEYS.TABLE_DATA_VIEW_SHOW_GRAND_TOTAL_FOR_ROWS,
  TABLE_DATA_SETTINGS_KEYS.TABLE_DATA_VIEW_VIRTUAL_MEASURES_SUMMARIZED_COLUMN_INDEX,
  TABLE_DATA_SETTINGS_KEYS.TABLE_DATA_VIEW_SHOW_ROW_NUMBERS,
  TABLE_DATA_SETTINGS_KEYS.TABLE_DATA_VIEW_USE_DISTINCT
];
const COMPOSITE_COMMAND_KEY = 'COMPOSITE_COMMAND';
const CHANGE_DATA_VIEW_SETTINGS_KEY = 'CHANGE_DATA_VIEW_SETTINGS';

export class Command<P> {

  static CHANGE_WORKBOOK_SETTINGS = commandFunction<ChangeWorkbookSettingsPayload>('CHANGE_WORKBOOK_SETTINGS');

  static ADD_WORKSHEET = commandFunction<AddWorksheetCommandPayload>('ADD_WORKSHEET');
  static COPY_WORKSHEET = commandFunction<CopyWorksheetCommandPayload>('COPY_WORKSHEET');
  static CHANGE_WORKSHEET_SETTINGS = commandFunction<ChangeWorksheetSettingsCommandPayload>('CHANGE_WORKSHEET_SETTINGS');
  static REMOVE_WORKSHEET = commandFunction<RemoveWorksheetCommandPayload>('REMOVE_WORKSHEET');
  static RENAME_WORKSHEET = commandFunction<RenameWorksheetCommandPayload>('RENAME_WORKSHEET');
  static REORDER_WORKSHEETS = commandFunction<ReorderWorksheetsCommandPayload>('REORDER_WORKSHEETS');

  static ADD_VIEW = commandFunction<AddViewCommandPayload>('ADD_VIEW');
  static ADD_NEW_VIEW_WITH_DATA_VIEW = commandFunction<AddNewViewWithDataViewCommandPayload>('ADD_NEW_VIEW_WITH_DATA_VIEW');
  static EXTRACT_DATA_VIEW_TO_NEW_VIEW = commandFunction<ExtractDataViewToNewViewCommandPayload>('EXTRACT_DATA_VIEW_TO_NEW_VIEW');
  static REMOVE_VIEW = commandFunction<RemoveViewCommandPayload>('REMOVE_VIEW');
  static COPY_VIEW = commandFunction<CopyViewCommandPayload>('COPY_VIEW');
  static SET_VIEW_LAYOUT = commandFunction<SetViewLayoutCommandPayload>('SET_VIEW_LAYOUT');
  static CHANGE_VIEW_SETTINGS = commandFunction<ChangeViewSettingsCommandPayload>('CHANGE_VIEW_SETTINGS');

  static ADD_DATA_VIEW = commandFunction<AddDataViewCommandPayload>('ADD_DATA_VIEW');
  static REMOVE_DATA_VIEW = commandFunction<RemoveDataViewCommandPayload>('REMOVE_DATA_VIEW');
  static CHANGE_VISUALIZATION = commandFunction<ChangeVisualizationCommandPayload>('CHANGE_VISUALIZATION');
  static CHANGE_DATA_VIEW_SETTINGS = commandFunction<ChangeDataViewSettingsCommandPayload>('CHANGE_DATA_VIEW_SETTINGS');
  static CHANGE_MAIN_OBJECTTYPE = commandFunction<ChangeMainObjecttypeCommandPayload>('CHANGE_MAIN_OBJECTTYPE');

  static ADD_OBJECTTYPES = commandFunction<AddObjecttypesCommandPayload>('ADD_OBJECTTYPES');
  static REMOVE_OBJECTTYPES = commandFunction<RemoveObjecttypesCommandPayload>('REMOVE_OBJECTTYPES');

  static ADD_ATTRIBUTE = commandFunction<AddAttributeCommandPayload>('ADD_ATTRIBUTE');
  static REMOVE_ATTRIBUTE = commandFunction<RemoveAttributeCommandPayload>('REMOVE_ATTRIBUTE');
  static REORDER_ATTRIBUTES = commandFunction<ReorderAttributesCommandPayload>('REORDER_ATTRIBUTES');

  static SET_DATA_VIEW_ATTRIBUTE_MODIFICATION = commandFunction<SetDataViewAttributeModificationCommandPayload>('SET_DATA_VIEW_ATTRIBUTE_MODIFICATION');
  static REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION = commandFunction<RemoveDataViewAttributeModificationCommandPayload>('REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION');
  static RESET_DATA_VIEW_ATTRIBUTE_MODIFICATION = commandFunction<ResetDataViewAttributeModificationCommandPayload>('RESET_DATA_VIEW_ATTRIBUTE_MODIFICATION');
  static SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES = commandFunction<SetDataViewAttributeHighlightingRuleCommandPayload>('SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES');
  static SORT_DATA_VIEW_BY_ATTRIBUTE = commandFunction<SortDataViewByAttributeCommandPayload>('SORT_DATA_VIEW_BY_ATTRIBUTE');

  static ADD_CONDITION = commandFunction<AddConditionCommandPayload>('ADD_CONDITION');
  static SET_CONDITION = commandFunction<SetConditionCommandPayload>('SET_CONDITION');
  static SET_CONDITION_SETTING = commandFunction<SetConditionSettingCommandPayload>('SET_CONDITION_SETTING');
  static REORDER_CONDITIONS = commandFunction<ReorderConditionsCommandPayload>('REORDER_CONDITIONS');
  static REMOVE_CONDITION = commandFunction<RemoveConditionCommandPayload>('REMOVE_CONDITION');
  static CONVERT_CONDITION_TO_QL = commandFunction<ConvertConditionToQlCommandPayload>('CONVERT_CONDITION_TO_QL');
  static ADD_SPATIAL_CONDITION = commandFunction<AddSpatialConditionCommandPayload>('ADD_SPATIAL_CONDITION');
  static SET_SPATIAL_CONDITION = commandFunction<SetSpatialConditionCommandPayload>('SET_SPATIAL_CONDITION');
  static REMOVE_GEOMETRY_FROM_SPATIAL_CONDITION = commandFunction<RemoveGeometryFromSpatialConditionCommandPayload>('REMOVE_GEOMETRY_FROM_SPATIAL_CONDITION');

  static CHANGE_CONDITION_SETTINGS = commandFunction<ChangeConditionSettingsCommandPayload>('CHANGE_CONDITION_SETTINGS');
  static ADD_CONDITION_GROUP = commandFunction<AddConditionGroupCommandPayload>('ADD_CONDITION_GROUP');
  static UNGROUP_CONDITION_GROUP = commandFunction<UngroupConditionGroupCommandPayload>('UNGROUP_CONDITION_GROUP');

  static CHANGE_CONDITION_GROUP_SETTINGS = commandFunction<ChangeConditionGroupSettingsCommandPayload>('CHANGE_CONDITION_GROUP_SETTINGS');
  static ADD_DATA_VIEW_CONDITION = commandFunction<AddDataViewConditionCommandPayload>('ADD_DATA_VIEW_CONDITION');
  static REORDER_DATA_VIEW_CONDITIONS = commandFunction<ReorderDataViewConditionSCommandPayload>('REORDER_DATA_VIEW_CONDITIONS');
  static REMOVE_DATA_VIEW_CONDITION = commandFunction<RemoveDataViewConditionCommandPayload>('REMOVE_DATA_VIEW_CONDITION');
  static CHANGE_DATA_VIEW_CONDITION = commandFunction<ChangeDataViewConditionCommandPayload>('CHANGE_DATA_VIEW_CONDITION');
  static CHANGE_DATA_VIEW_CONDITION_SETTING = commandFunction<ChangeDataViewConditionSettingCommandPayload>('CHANGE_DATA_VIEW_CONDITION_SETTING');

  static ADD_QL_CONDITION = commandFunction<AddQlConditionCommandPayload>('ADD_QL_CONDITION');
  static SET_QL_CONDITION = commandFunction<SetQlConditionCommandPayload>('SET_QL_CONDITION');

  static CHANGE_HIERARCHY_LEVEL = commandFunction<ChangeHierarchyLevelCommandPayload>('CHANGE_HIERARCHY_LEVEL');

  static ADD_DRILLTHROUGH = commandFunction<AddDrillThroughCommandPayload>('ADD_DRILLTHROUGH');
  static EDIT_DRILLTHROUGH = commandFunction<EditDrillThroughCommandPayload>('EDIT_DRILLTHROUGH');
  static REMOVE_DRILLTHROUGH = commandFunction<RemoveDrillThroughCommandPayload>('REMOVE_DRILLTHROUGH');

  static SET_WORKBOOK_ATTRIBUTE_MODIFICATION = commandFunction<SetWorkbookAttributeModificationCommandPayload>('SET_WORKBOOK_ATTRIBUTE_MODIFICATION');
  static REMOVE_WORKBOOK_ATTRIBUTE_MODIFICATION = commandFunction<RemoveWorkbookAttributeModificationCommandPayload>('REMOVE_WORKBOOK_ATTRIBUTE_MODIFICATION');
  static RESET_WORKBOOK_ATTRIBUTE_MODIFICATION = commandFunction<ResetWorkbookAttributeModificationCommandPayload>('RESET_WORKBOOK_ATTRIBUTE_MODIFICATION');

  static CREATE_LOCAL_DATE_HIERARCHY = commandFunction<CreateLocalDateHierarchyCommandPayload>('CREATE_LOCAL_DATE_HIERARCHY');
  static CREATE_LOCAL_ATTRIBUTE_WITH_MODIFIED_OLAP_ATTRIBUTE_DEFINITION = commandFunction<CreateLocalAttributeWithModifiedOlapAttributeDefinitionCommandPayload>('CREATE_LOCAL_ATTRIBUTE_WITH_MODIFIED_OLAP_ATTRIBUTE_DEFINITION');
  static REMOVE_LOCAL_ATTRIBUTE = commandFunction<RemoveLocalAttributeCommandPayload>('REMOVE_LOCAL_ATTRIBUTE');

  static IMPORT_LAYER_FROM_REPOSITORY = commandFunction<ImportLayerFromRepositoryCommandPayload>('IMPORT_LAYER_FROM_REPOSITORY');
  static CHANGE_LAYER_SETTINGS = commandFunction<ChangeLayerSettingsCommandPayload>('CHANGE_LAYER_SETTINGS');
  static REORDER_LAYERS = commandFunction<ReorderLayersCommandPayload>('REORDER_LAYERS');

  static ADD_ACQUISITION_LAYER = commandFunction<AddAcquisitionLayerCommandPayload>('ADD_ACQUISITION_LAYER');

  static SET_SELECTION = commandFunction<SetSelectionCommandPayload>('SET_SELECTION');
  static INVERT_SELECTION = commandFunction<InvertSelectionCommandPayload>('INVERT_SELECTION');
  static CREATE_FILTER_FROM_SELECTION = commandFunction<CreateFilterFromSelectionCommandPayload>('CREATE_FILTER_FROM_SELECTION');
  static DRILL_DOWN_FROM_SELECTION = commandFunction<DrillDownFromSelectionCommandPayload>('DRILL_DOWN_FROM_SELECTION');
  static TRANSFER_CONDITIONS = commandFunction<TransferConditionsCommandPayload>('TRANSFER_CONDITIONS');
  static TRANSFER_SELECTION_FROM_DRILL_THROUGH = commandFunction<TransferSelectionFromDrillThroughCommandPayload>('TRANSFER_SELECTION_FROM_DRILL_THROUGH');

  static REMOVE_LAYER = commandFunction<RemoveLayerCommandPayload>('REMOVE_LAYER');

  static ADD_WMS_LAYER = commandFunction<AddWmsLayerCommandPayload>('ADD_WMS_LAYER');

  static CREATE_GEOCODING_REPORT = commandFunction<CreateGeocodingReportCommandPayload>('CREATE_GEOCODING_REPORT');

  static ADD_SKETCH_LAYER = commandFunction<AddSketchLayerCommandPayload>('ADD_SKETCH_LAYER');

  static CHANGE_ATTRIBUTE_BUNDLE_SETTINGS = commandFunction<ChangeAttributeBundleSettingsPayload>('CHANGE_ATTRIBUTE_BUNDLE_SETTINGS');

  static COMPOSITE_COMMAND = commandFunction<CompositeCommandPayload>('COMPOSITE_COMMAND');

  static {
    // Replace all Command types with real object functions
    // @ts-ignore – Command magic
    Object.keys(Command).forEach(type => { Command[type].type = type; });
    Object.freeze(Command);
  }

  constructor (type: string, payload: P) {
    assert(type, 'type is mandatory');
    assert(type in Command, `unknown command type ${type}`);
    this.type = type;
    this.payload = payload;
    this.majorChange = this._isMajorChange(type, payload);
  }

  /** the type of the command as {string} */
  type: string;

  /** payload The data to send with the command */
  payload: P;

  /**
   * Whether the command made a change that is considered major. If so, an alert will be
   * shown when navigating away from the workbook tab, for example when closing it.
   */
  majorChange: boolean;

  /**
   * Creates a {@link Command.COMPOSITE_COMMAND} of this and the given command in that order.
   * If this command is an {@link Command.COMPOSITE_COMMAND} already, the given command is
   * added to the tail of the containing commands.
   *
   * @param command - The command to append as a follow-up command
   * @return A {@link Command.COMPOSITE_COMMAND} command
   */
  append (command: Command<P>): Command<CompositeCommandPayload> {
    if (this.type === Command.COMPOSITE_COMMAND.type) {
      const compositeCommand = this as unknown as Command<CompositeCommandPayload>;
      compositeCommand.payload.commands.push(command);
      return compositeCommand;
    } else {
      return Command.COMPOSITE_COMMAND({ commands: [ this, command ] });
    }
  }

  _isMajorChange (type: string, payload: P) {
    if (type === COMPOSITE_COMMAND_KEY) {
      return this._isCompositeCommandMajorChange(payload as unknown as CompositeCommandPayload);
    } else if (type === CHANGE_DATA_VIEW_SETTINGS_KEY) {
      return this._isChangeDataViewSettingsMajorChange(payload as unknown as ChangeDataViewSettingsCommandPayload);
    }
    return !MINOR_COMMANDS.includes(type);
  }

  _isCompositeCommandMajorChange (payload: CompositeCommandPayload) {
    return Number(payload.commands?.filter(command => command.majorChange).length) > 0;
  }

  _isChangeDataViewSettingsMajorChange (payload: ChangeDataViewSettingsCommandPayload) {
    // ! because we checked for CHANGE_DATA_VIEW_SETTINGS_KEY before
    const settingsKeys = Object.keys(payload.settings!);
    const allMinorKeys = settingsKeys.filter((key) => MINOR_DATA_VIEW_SETTINGS_KEYS.includes(key));
    return allMinorKeys.length !== settingsKeys.length;
  }

}

function commandFunction<P> (type: string) {
  const func = (payload: P) => new Command<P>(type, payload);
  func.type = type;
  return func;
}
